<template>
  <nav v-if="totalPages > 1" class="pagination mt-4" role="navigation" aria-label="pagination">
    <a
      :disabled="previousPage ? undefined : true"
      class="pagination-previous"
      :href="previousPage ? pageLink(previousPage) : undefined"
      @click.prevent="previousPage && linkSelected(previousPage)"
    >
      ← Previous
    </a>
    <a
      :disabled="nextPage ? undefined : true"
      class="pagination-next"
      :href="nextPage ? pageLink(nextPage) : undefined"
      @click.prevent="nextPage && linkSelected(nextPage)"
    >
      Next →
    </a>

    <ul class="pagination-list">
      <li v-for="page in linkList" :key="page">
        <span v-if="page === '...'" class="pagination-ellipsis">&hellip;</span>
        <a
          v-else
          class="pagination-link"
          :class="{ 'is-current': page == currentPage }"
          aria-label="`Goto page ${index}`"
          :href="pageLink(page)"
          @click.prevent="linkSelected(page)"
          >{{ page }}</a
        >
      </li>
    </ul>
  </nav>
</template>

<script setup>import { computed as _computed } from 'vue';

  import { range } from "lodash-es";

  const props = defineProps({
    modelValue: { type: Number, default: 1 },
    totalEntries: { type: Number, default: 0 },
    perPage: { type: Number, default: 20 },
    baseHref: { type: String, default: null },
    innerWindow: { type: Number, default: 4 },
    outerWindow: { type: Number, default: 1 },
  });

  const emit = defineEmits(["update:model-value"]);

  const totalPages = _computed(() => Math.ceil(props.totalEntries / props.perPage));

  const firstPage = 1;
  const lastPage = _computed(() => totalPages.value);
  const nextPage = _computed(() => (currentPage.value === lastPage.value ? null : currentPage.value + 1));
  const previousPage = _computed(() => (currentPage.value === firstPage ? null : currentPage.value - 1));

  const currentPage = _computed(() => {
    if (props.modelValue < 1) {
      return 1;
    } else if (props.modelValue > lastPage.value) {
      return lastPage.value;
    } else {
      return +props.modelValue;
    }
  });

  const pageLink = (pageNumber) => {
    if (props.baseHref === null) {
      return `#?page=${pageNumber}`;
    } else {
      return `?page=${pageNumber}`;
    }
  };

  /* The following method is derived from will_paginate:
   *  Copyright (c) 2009 Mislav Marohnić
   *  https://github.com/mislav/will_paginate/blob/master/LICENSE
   */
  const linkList = _computed(() => {
    let windowFrom = currentPage.value - props.innerWindow;
    let windowTo = currentPage.value + props.innerWindow;

    if (windowTo > totalPages.value) {
      windowFrom -= windowTo - totalPages.value;
      windowTo = totalPages.value;
    }

    if (windowFrom < 1) {
      windowTo += 1 - windowFrom;
      windowFrom = 1;
      if (windowTo > totalPages.value) {
        windowTo = totalPages.value;
      }
    }

    const middle = range(windowFrom, windowTo + 1);

    const left =
      props.outerWindow + 3 < middle[0]
        ? [...range(1, props.outerWindow + 2), "..."]
        : range(1, middle[0]);

    const right =
      totalPages.value - props.outerWindow - 2 > middle[middle.length - 1]
        ? ["...", ...range(totalPages.value - props.outerWindow, totalPages.value + 1)]
        : range(middle[middle.length - 1] + 1, totalPages.value + 1);

    return [...left, ...middle, ...right];
  });

  const linkSelected = (pageNumber) => {
    emit("update:model-value", pageNumber);
  };
</script>
